import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { ScrollArea, ScrollAreaViewport, ScrollAreaScrollbar } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { GripVertical, Loader2 } from "lucide-react";
import { type DisputeColumnConfig, DEFAULT_DISPUTE_COLUMNS } from "@/hooks/useDisputeColumnPreferences";

interface DisputeColumnConfigDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  columns: DisputeColumnConfig[];
  onColumnsChange: (columns: DisputeColumnConfig[]) => Promise<void>;
  onResetToDefaults: () => Promise<void>;
  isSaving: boolean;
}

export function DisputeColumnConfigDialog({
  open,
  onOpenChange,
  columns,
  onColumnsChange,
  onResetToDefaults,
  isSaving,
}: DisputeColumnConfigDialogProps) {
  const [localColumns, setLocalColumns] = useState<DisputeColumnConfig[]>(columns);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  useEffect(() => {
    setLocalColumns(columns);
  }, [columns]);

  const handleToggleColumn = (columnId: string) => {
    setLocalColumns((prev) => {
      const visibleCount = prev.filter((x) => x.visible).length;
      const column = prev.find((x) => x.id === columnId);

      if (column?.visible && visibleCount === 1) {
        toast.error("At least one column must be visible");
        return prev;
      }

      return prev.map((col) =>
        col.id === columnId ? { ...col, visible: !col.visible } : col
      );
    });
  };

  const handleSelectAll = () => {
    setLocalColumns((prev) => prev.map((col) => ({ ...col, visible: true })));
  };

  const handleDeselectAll = () => {
    setLocalColumns((prev) =>
      prev.map((col, index) => ({
        ...col,
        visible: index === 0,
      }))
    );
    toast.info("At least one column must remain visible");
  };

  const handleResetToDefault = async () => {
    await onResetToDefaults();
    setLocalColumns(DEFAULT_DISPUTE_COLUMNS);
  };

  const handleApply = async () => {
    const reorderedColumns = localColumns.map((col, index) => ({
      ...col,
      order: index,
    }));
    await onColumnsChange(reorderedColumns);
    onOpenChange(false);
  };

  const handleCancel = () => {
    setLocalColumns(columns);
    onOpenChange(false);
  };

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();

    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    const newColumns = [...localColumns];
    const [draggedColumn] = newColumns.splice(draggedIndex, 1);
    newColumns.splice(dropIndex, 0, draggedColumn);

    setLocalColumns(newColumns);
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const visibleColumns = localColumns.filter((col) => col.visible);
  const defaultColumns = localColumns.filter((col) => col.isDefault);
  const additionalColumns = localColumns.filter((col) => !col.isDefault);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>Configure Table Columns</DialogTitle>
          <DialogDescription>
            Select which columns to display and drag to reorder them. Your preferences will be saved.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleSelectAll} disabled={isSaving}>
              Select All
            </Button>
            <Button variant="outline" size="sm" onClick={handleDeselectAll} disabled={isSaving}>
              Deselect All
            </Button>
            <Button variant="outline" size="sm" onClick={handleResetToDefault} disabled={isSaving}>
              Reset to Default
            </Button>
          </div>

          <Separator />

          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-muted-foreground">Available Columns</h3>
              <ScrollArea className="h-[400px]">
                <ScrollAreaViewport className="h-full">
                  <div className="space-y-6 pr-4">
                    <div>
                      <h4 className="text-xs font-medium mb-3 text-muted-foreground uppercase">
                        Default Columns
                      </h4>
                      <div className="space-y-2">
                        {defaultColumns.map((column) => (
                          <div
                            key={column.id}
                            className="flex items-center space-x-3 p-2 rounded-md hover:bg-muted/50 transition-colors"
                          >
                            <Checkbox
                              id={column.id}
                              checked={column.visible}
                              onCheckedChange={() => handleToggleColumn(column.id)}
                              disabled={isSaving}
                            />
                            <Label
                              htmlFor={column.id}
                              className="text-sm font-medium leading-none cursor-pointer flex-1"
                            >
                              {column.label}
                            </Label>
                          </div>
                        ))}
                      </div>
                    </div>

                    {additionalColumns.length > 0 && (
                      <>
                        <Separator />
                        <div>
                          <h4 className="text-xs font-medium mb-3 text-muted-foreground uppercase">
                            Additional Columns
                          </h4>
                          <div className="space-y-2">
                            {additionalColumns.map((column) => (
                              <div
                                key={column.id}
                                className="flex items-center space-x-3 p-2 rounded-md hover:bg-muted/50 transition-colors"
                              >
                                <Checkbox
                                  id={column.id}
                                  checked={column.visible}
                                  onCheckedChange={() => handleToggleColumn(column.id)}
                                  disabled={isSaving}
                                />
                                <Label
                                  htmlFor={column.id}
                                  className="text-sm font-medium leading-none cursor-pointer flex-1"
                                >
                                  {column.label}
                                </Label>
                              </div>
                            ))}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </ScrollAreaViewport>
                <ScrollAreaScrollbar orientation="vertical" />
              </ScrollArea>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-muted-foreground">Column Order</h3>
                <span className="text-xs text-muted-foreground">Drag to reorder</span>
              </div>
              <ScrollArea className="h-[400px]">
                <ScrollAreaViewport className="h-full">
                  {visibleColumns.length === 0 ? (
                    <div className="flex items-center justify-center h-32 text-sm text-muted-foreground">
                      No columns selected
                    </div>
                  ) : (
                    <div className="space-y-2 pr-4">
                      {visibleColumns.map((column, index) => (
                        <div
                          key={column.id}
                          draggable={!isSaving}
                          onDragStart={() => handleDragStart(localColumns.indexOf(column))}
                          onDragOver={(e) => handleDragOver(e, localColumns.indexOf(column))}
                          onDragLeave={handleDragLeave}
                          onDrop={(e) => handleDrop(e, localColumns.indexOf(column))}
                          onDragEnd={handleDragEnd}
                          className={`
                            flex items-center gap-3 p-3 rounded-md border bg-card
                            cursor-move transition-all
                            ${draggedIndex === localColumns.indexOf(column) ? "opacity-50" : "opacity-100"}
                            ${dragOverIndex === localColumns.indexOf(column) && draggedIndex !== localColumns.indexOf(column) ? "border-primary border-2" : "border-border"}
                            hover:bg-muted/50 hover:shadow-sm
                          `}
                        >
                          <GripVertical className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-mono text-muted-foreground w-6">
                                {index + 1}
                              </span>
                              <span className="text-sm font-medium truncate">{column.label}</span>
                            </div>
                          </div>
                          <button
                            onClick={() => handleToggleColumn(column.id)}
                            className="text-muted-foreground hover:text-destructive transition-colors flex-shrink-0"
                            title="Remove column"
                            disabled={isSaving}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <line x1="18" y1="6" x2="6" y2="18"></line>
                              <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollAreaViewport>
                <ScrollAreaScrollbar orientation="vertical" />
              </ScrollArea>
            </div>
          </div>

          <Separator />

          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <span>
              {visibleColumns.length} of {localColumns.length} columns selected
            </span>
            {visibleColumns.length > 0 && (
              <span className="text-xs">Columns will appear in the order shown on the right</span>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleApply} disabled={isSaving}>
            {isSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              "Apply Changes"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
